<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Figma 7415:16445: the title 24 under the logo row, Continue in the page 24 under the list. -->
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="var(--size-24)">
				<Text font="--heading-h3" color="--text-dark-primary">How likely are you to continue with Kova?</Text>

				<!-- :commit-delay-ms="0": DS ≥0.36 delays single-select commits 250 ms by default;
     the selection gates the Continue CTA, so the commit must be instant. -->
				<Select type="single" :value="selected" gap="var(--size-8)" :commit-delay-ms="0" @ds:change="onChange">
					<template #options>
						<SelectItem v-for="o in LIKELIHOOD" :key="o.value" :value="o.value">
							<Text font="--text-16-20-semi" color="--text-dark-primary">{{ o.label }}</Text>
							<SelectIndicator mode="always" />
						</SelectItem>
					</template>
				</Select>

				<Button
					full-width
					:disabled="(!geo.isOneClickCancelRequired && !selected) || busy"
					:loading="busy"
					@ds:click="next"
				>
					<Text font="--text-16-18-semi">Continue</Text>
				</Button>
			</LayoutFlex>
		</template>
	</LayoutScreen>
	<!-- The cancel call failed (500 / no connection): nothing was cancelled. "Try again" re-runs it here. -->
	<Modal :visible="cancelFailed" fullscreen animation="none">
		<CancelErrorScreen :step="step.title" @retry="retryRenewalsOnly" />
	</Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
	Button,
	LayoutFlex,
	LayoutScreen,
	Modal,
	Select,
	SelectIndicator,
	SelectItem,
	Text,
} from '@simple-life-app/growth-ui-kova'
import CancelErrorScreen from '@/components/cancel/CancelErrorScreen.vue'
import CancelHeader from '@/components/CancelHeader.vue'
import { afterLikelihood } from '@/cancelFlow/commitment'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useGeoStore } from '@/store/geo'
import { useSubscriptionStore } from '@/store/subscription'
import { useSurveyStore } from '@/store/survey'
import { STEP_TITLE, stateChangeOnCancel, useCancelStepLogging } from '@/analytics'

const router = useRouter()
const survey = useSurveyStore()
const geo = useGeoStore()
const flow = useCancelFlowStore()
const subs = useSubscriptionStore()
const busy = ref(false)
const cancelFailed = ref(false)

// Figma `kova cancel flow3` (28386:9153, now 7415:16445). 5-point likelihood, descending.
const LIKELIHOOD = [
	{ value: 'very_likely', label: 'Very likely' },
	{ value: 'somewhat_likely', label: 'Somewhat likely' },
	{ value: 'neither_likely_or_unlikely', label: 'Neither likely or unlikely' },
	{ value: 'somewhat_unlikely', label: 'Somewhat unlikely' },
	{ value: 'very_unlikely', label: 'Very unlikely' },
] as const

const selected = computed<string>(() => {
	const raw = survey.answers['return_likelihood']
	return typeof raw === 'string' ? raw : ''
})

const step = useCancelStepLogging(STEP_TITLE.TRY_AGAIN, {
	getResponses: () => (selected.value ? [selected.value] : []),
})

function onChange(next: string | string[]) {
	const value = Array.isArray(next) ? (next[0] ?? '') : next
	survey.setAnswer('return_likelihood', value)
	step.record(value ? [value] : [])
}

async function next() {
	const stage = flow.stage
	// Not a commitment: the classic chain, unchanged.
	if (!stage) {
		router.push('/cancel/confirm')
		return
	}
	const after = afterLikelihood(stage)
	if (after.kind === 'route') {
		router.push(after.to)
		return
	}
	// Commitment under review, "Cancel future renewals only" (DEV-35727): Figma has no confirm step
	// here — this Continue is the `soft` cancel, and the receipt says the renewals are off.
	if (busy.value) return
	busy.value = true
	const { price, currency, keep, endOfPeriod } = flow.quotes
	// Paid in full (owner, 2026-10-21): no cycle to wind down — a plain `soft`, the period already paid.
	const upfront = flow.kind === 'upfront'
	const changed = stateChangeOnCancel(upfront ? 'soft' : 'soft_commitment')
	try {
		await subs.cancel({ cancellationType: 'soft' })
		changed()
		// The backend's answer dates it (`soft_commitment` → cycle end + grace, `soft` → period end + grace);
		// the quote is the fallback.
		if (upfront) {
			flow.setOutcome({
				kind: 'paid_through',
				until: subs.subscription?.expired_at ?? endOfPeriod?.access_until ?? null,
			})
		} else {
			const until = subs.subscription?.expired_at ?? keep?.access_until ?? null
			flow.setOutcome({ kind: 'renewals_cancelled', price, currency, until })
		}
		survey.reset()
		router.push('/cancel/done')
	} catch {
		cancelFailed.value = true
	} finally {
		busy.value = false
	}
}

function retryRenewalsOnly() {
	cancelFailed.value = false
	next()
}
</script>
